import {AD_REFRESH_TRIGGERS, AdFoxConfig} from '@shared/Ads/Yandex/types';

import {FieldValidator, FieldValidatorBuilder} from './types';

export const getBoolean: FieldValidatorBuilder<boolean> = (
  fallback = false,
) => ({
  is: (value): value is boolean => typeof value === 'boolean',
  fallback,
});

export const getNumber: FieldValidatorBuilder<number> = (fallback = 0) => ({
  is: (value): value is number => typeof value === 'number',
  fallback,
});

export const getString: FieldValidatorBuilder<string> = (fallback = '') => ({
  is: (value): value is string => typeof value === 'string',
  fallback,
});

export const getStringUnion = <T extends string>(
  fallback: T,
  possibleValues: Array<T> | ReadonlyArray<T>,
): FieldValidator<T> => ({
  is: (value): value is T => {
    if (typeof value !== 'string') {
      return false;
    }
    return (possibleValues as ReadonlyArray<string>).includes(value);
  },
  fallback,
});

export const getStringMap: FieldValidatorBuilder<Record<string, string>> = (
  fallback = {},
) => ({
  is: (value): value is Record<string, string> => {
    if (typeof value !== 'object') return false;

    if (Array.isArray(value)) return false;

    if (Object.getOwnPropertySymbols(value).length > 0) return false;

    return Object.getOwnPropertyNames(value).every(
      (prop) => typeof value[prop] === 'string',
    );
  },
  fallback,
});

export const getBooleanNumberMap: FieldValidatorBuilder<
  Record<string, number | boolean>
> = (fallback = {}) => ({
  is: (value): value is Record<string, number | boolean> => {
    if (typeof value !== 'object') return false;

    if (Array.isArray(value)) return false;

    if (Object.getOwnPropertySymbols(value).length > 0) return false;

    return Object.getOwnPropertyNames(value).every(
      (prop) =>
        typeof value[prop] === 'number' || typeof value[prop] === 'boolean',
    );
  },
  fallback,
});

const isAdFoxLazyLoadConfig = (
  value: unknown,
): value is AdFoxConfig['lazyLoad'] => {
  if (typeof value === 'boolean') {
    return true;
  }
  if (!value || typeof value !== 'object') {
    return false;
  }
  return (
    'fetchMargin' in value &&
    typeof value.fetchMargin === 'number' &&
    (!('mobileScaling' in value) || typeof value.mobileScaling === 'number')
  );
};

const isAdFoxAutorefreshConfig = (
  value: unknown,
): value is AdFoxConfig['autorefresh'] => {
  if (!value || typeof value !== 'object') {
    return false;
  }
  return (
    'intervalMs' in value &&
    typeof value.intervalMs === 'number' &&
    'triggers' in value &&
    Array.isArray(value.triggers) &&
    value.triggers.every((item) => AD_REFRESH_TRIGGERS.includes(item))
  );
};

export const AdFoxConfigValidator: FieldValidator<AdFoxConfig> = {
  is: (value): value is AdFoxConfig => {
    if (typeof value !== 'object') {
      return false;
    }
    if (!('enabled' in value) || typeof value.enabled !== 'boolean') {
      return false;
    }
    if ('lazyLoad' in value && !isAdFoxLazyLoadConfig(value.lazyLoad)) {
      return false;
    }
    if (
      'autorefresh' in value &&
      !isAdFoxAutorefreshConfig(value.autorefresh)
    ) {
      return false;
    }
    if (
      'statSendingIntervalMs' in value &&
      typeof value.statSendingIntervalMs !== 'number'
    ) {
      return false;
    }
    return true;
  },
  fallback: {enabled: false},
};
